import { API_CONFIG } from "@/infrastructure/config/api";
import {
  createAuthenticatedSSE,
  type SSESubscription,
} from "@/shared/utils/fetchSSE";
import { get, throwFromResponse, makeApiError } from "@/shared/utils/fetchApi";
import type {
  MeetingHistoryResponse,
  MeetingEvent,
  Meeting,
  MeetingStatus,
} from "@/features/Assistant/types/meetingHistory";

const API_URL = API_CONFIG.API_URL;

const FILENAME_UTF8_RE = /filename\*=UTF-8''([^;]+)/i;
const FILENAME_RE = /filename="?([^";]+)"?/i;

export class MeetingHistoryService {
  /**
   * Busca histórico de reuniões via REST
   * @param startDate Data inicial (ISO string)
   * @param endDate Data final (ISO string)
   * @param limit Número máximo de eventos
   * @param offset Offset para paginação
   * @returns Promise com eventos de reuniões
   */
  static async getHistory(
    startDate?: string,
    endDate?: string,
    limit?: number,
    offset?: number,
  ): Promise<MeetingHistoryResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);
    // `!== undefined` e não truthy: `limit=0` e `offset=0` são valores válidos e
    // o teste por verdade os descartava.
    if (limit !== undefined) params.append("limit", limit.toString());
    if (offset !== undefined) params.append("offset", offset.toString());

    const qs = params.toString();
    return get<MeetingHistoryResponse>(`/assistant/meetings/history?${qs}`);
  }

  /**
   * Cria uma conexão SSE para receber atualizações de histórico em tempo real
   * @param period Período em dias (1, 7, 30)
   * @param onUpdate Callback para cada atualização
   * @param onError Callback para erros
   * @returns EventSource para controle da conexão
   */
  static createHistoryStream(
    period: number,
    onUpdate: (events: MeetingEvent[]) => void,
    onError?: (event: Event) => void,
  ): SSESubscription {
    const params = new URLSearchParams();
    params.append("period", period.toString());

    const url = `${API_CONFIG.API_ROOT_URL}/assistant/meetings/history/stream?${params.toString()}`;

    return createAuthenticatedSSE(url, {
      onMessage: (data) => {
        if (!data || data === ":") return;
        try {
          const parsed = JSON.parse(data);
          if (parsed.error) {
            if (onError) {
              onError(new ErrorEvent("error", { message: parsed.error }));
            }
            return;
          }
          const events = parsed.data || [];
          onUpdate(events);
        } catch {
          // Non-JSON SSE payload (e.g. keep-alive) — safe to ignore
        }
      },
      onError: (error) => {
        if (onError) {
          onError(new ErrorEvent("error", { error }));
        }
      },
    });
  }

  /**
   * Agrupa eventos por session_id e determina status da reunião
   * @param events Lista de eventos
   * @returns Lista de reuniões agrupadas
   */
  static groupBySession(events: MeetingEvent[]): Meeting[] {
    const sessionMap = new Map<string, Meeting>();

    for (const evt of events) {
      const sessionId = evt.metadata?.session_id || evt.id;

      if (!sessionMap.has(sessionId)) {
        sessionMap.set(sessionId, {
          sessionId,
          meetingName: evt.metadata?.meeting_name || null,
          meetingUrl: evt.metadata?.meeting_url || null,
          fromEmail: evt.metadata?.from_email || null,
          events: [],
          latestStatus: "scheduled",
          latestTime: evt.created_at,
        });
      }

      sessionMap.get(sessionId)!.events.push(evt);
    }

    // Processar cada reunião
    const meetings = Array.from(sessionMap.values());

    for (const meeting of meetings) {
      // Ordenar eventos por created_at
      meeting.events.sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );

      // Determinar status da reunião
      meeting.latestStatus = this.deriveMeetingStatus(meeting.events);

      // Última data
      const lastEvent = meeting.events[meeting.events.length - 1];
      meeting.latestTime = lastEvent.created_at;

      // Pegar meeting_name do primeiro evento que tiver
      if (!meeting.meetingName) {
        for (const e of meeting.events) {
          if (e.metadata?.meeting_name) {
            meeting.meetingName = e.metadata.meeting_name;
            break;
          }
        }
      }
    }

    // Filtrar apenas reuniões que realmente começaram (não apenas scheduled)
    const activeMeetings = meetings.filter((m) =>
      m.events.some((e) => e.event_type !== "meeting_scheduled"),
    );

    // Ordenar por data mais recente primeiro
    return activeMeetings.sort(
      (a, b) =>
        new Date(b.latestTime).getTime() - new Date(a.latestTime).getTime(),
    );
  }

  /**
   * Deriva o status da reunião baseado nos eventos
   * @param events Lista de eventos da reunião
   * @returns Status da reunião
   */
  private static deriveMeetingStatus(events: MeetingEvent[]): MeetingStatus {
    const types = events.map((e) => e.event_type);
    const hasError = events.some((e) => e.event_type === "meeting_error");
    const hasTranscription = types.includes("meeting_transcription");
    const hasLeft = types.includes("meeting_left");
    const hasJoined = types.includes("meeting_joined");

    if (hasError && !hasTranscription) return "error";
    if (hasTranscription) return "delivered";
    if (hasLeft) return "completed";
    if (hasJoined) return "in_progress";
    return "scheduled";
  }

  /**
   * Downloads an archived transcript via the apps/api proxy and triggers a
   * browser save dialog. Mirrors the same fetch → blob → anchor click pattern
   * used by the Knowledge Base file download — no direct Supabase URL ever
   * reaches the browser, so the internal-only storage hostname is not exposed.
   */
  static async downloadTranscript(storagePath: string): Promise<void> {
    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) {
      throw makeApiError("No authentication token provided", {
        status: 401,
        code: "E_INVALID_TOKEN",
      });
    }

    const params = new URLSearchParams();
    params.append("path", storagePath);
    const url = `${API_URL}/admin/assistant/meetings/transcript?${params.toString()}`;

    const response = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!response.ok) {
      await throwFromResponse(response);
    }

    const disposition = response.headers.get("Content-Disposition") || "";
    let fileName = storagePath.split("/").pop() || "transcript.json";
    const utf8 = disposition.match(FILENAME_UTF8_RE);
    if (utf8?.[1]) {
      try {
        fileName = decodeURIComponent(utf8[1]);
      } catch {
        /* keep fallback */
      }
    } else {
      const plain = disposition.match(FILENAME_RE);
      if (plain?.[1]) fileName = plain[1];
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    try {
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      window.URL.revokeObjectURL(blobUrl);
    }
  }

  /**
   * Formata duração em segundos para string legível
   * @param seconds Duração em segundos
   * @returns String formatada (ex: "30m 27s")
   */
  static formatDuration(seconds: number): string {
    if (!seconds) return "-";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  }
}
